// Massenbearbeitung: Status, Bearbeiter oder ein Feld für alle markierten bzw. alle Treffer setzen. Erst Anzahl und
// Änderung zeigen, dann nach Bestätigung in Stapeln von 25 nach Close schreiben (Schlüssel des Nutzers), mit Fortschritt.
import { useEffect, useRef, useState } from "react";
import { fehlerText, type FeldWert } from "../../api";
import { massenApi, type BearbeitungErgebnis, type Ziel, type ZielAntwort } from "../../api/leadsNeu";
import { useMeta } from "../../daten/meta";
import { istLeer } from "../../werkzeuge/felder";
import { Auswahl } from "../Auswahl";
import { Dialog } from "../Dialog";
import { FeldEingabe } from "../FeldEingabe";
import { Icon } from "../Icon";
import "./smartview.css";

type Art = "status" | "bearbeiter" | "feld";
type Schritt = "eingabe" | "bestaetigen" | "lauf" | "ergebnis";

const STAPEL = 25;

export function MassenbearbeitungDialog({
  offen,
  onSchliessen,
  ziel,
  titel,
  beschreibung,
  onFertig,
}: {
  offen: boolean;
  onSchliessen: () => void;
  ziel: Ziel;
  titel: string;
  /** z. B. „alle Treffer“ bzw. „12 markierte Leads“ */
  beschreibung: string;
  onFertig?: () => void;
}) {
  const { meta, nutzerListe, nutzerName, status: statusVon } = useMeta();
  const [schritt, setSchritt] = useState<Schritt>("eingabe");
  const [art, setArt] = useState<Art>("status");
  const [statusId, setStatusId] = useState<string | null>(null);
  const [bearbeiter, setBearbeiter] = useState<string | null>(null);
  const [bearbeiterLeeren, setBearbeiterLeeren] = useState(false);
  const [feldId, setFeldId] = useState<string | null>(null);
  const [feldWert, setFeldWert] = useState<FeldWert>(null);
  const [zielAntwort, setZielAntwort] = useState<ZielAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [ergebnisse, setErgebnisse] = useState<BearbeitungErgebnis[]>([]);
  const [fortschritt, setFortschritt] = useState({ fertig: 0, gesamt: 0 });
  const [abgebrochen, setAbgebrochen] = useState(false);
  const abbruch = useRef(false);

  const bearbeiterFeld = meta?.custom_fields.lead.find((f) => f.type === "user" && f.name.trim().toLowerCase() === "bearbeiter");
  const felder = (meta?.custom_fields.lead ?? []).filter((f) => f.type !== "contact" && f.type !== "hidden" && f.id !== bearbeiterFeld?.id);
  const feld = felder.find((f) => f.id === feldId) ?? null;

  useEffect(() => {
    if (!offen) return;
    abbruch.current = false;
    setSchritt("eingabe");
    setArt("status");
    setStatusId(null);
    setBearbeiter(null);
    setBearbeiterLeeren(false);
    setFeldId(null);
    setFeldWert(null);
    setZielAntwort(null);
    setFehler(null);
    setErgebnisse([]);
    setAbgebrochen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  const eingabeOk = art === "status" ? !!statusId : art === "bearbeiter" ? !!bearbeiter || bearbeiterLeeren : !!feld;

  const aktion = () =>
    art === "status"
      ? { status_id: statusId }
      : art === "bearbeiter"
        ? { bearbeiter_id: bearbeiterLeeren ? "" : bearbeiter }
        : { custom: { [feldId as string]: istLeer(feldWert) ? "" : feldWert } };

  const aenderungText = () =>
    art === "status"
      ? `Status → „${statusVon(statusId)?.label ?? statusId}“`
      : art === "bearbeiter"
        ? bearbeiterLeeren
          ? "Bearbeiter entfernen"
          : `Bearbeiter → ${nutzerName(bearbeiter)}`
        : `${feld?.name} → ${istLeer(feldWert) ? "(leer, Wert entfernen)" : Array.isArray(feldWert) ? feldWert.join(", ") : String(feldWert)}`;

  const weiter = async () => {
    setLaedt(true);
    setFehler(null);
    try {
      setZielAntwort(await massenApi.ziel(ziel));
      setSchritt("bestaetigen");
    } catch (e) {
      setFehler(fehlerText(e));
    }
    setLaedt(false);
  };

  const ausfuehren = async () => {
    if (!zielAntwort?.lead_ids.length) return;
    abbruch.current = false;
    const ids = zielAntwort.lead_ids;
    const alle: BearbeitungErgebnis[] = [];
    setErgebnisse([]);
    setAbgebrochen(false);
    setFehler(null);
    setFortschritt({ fertig: 0, gesamt: ids.length });
    setSchritt("lauf");
    try {
      for (let i = 0; i < ids.length; i += STAPEL) {
        if (abbruch.current) throw new Error("abgebrochen");
        const r = await massenApi.ausfuehren(ids.slice(i, i + STAPEL), aktion());
        alle.push(...r.data);
        setErgebnisse([...alle]);
        setFortschritt({ fertig: alle.length, gesamt: ids.length });
      }
    } catch (e) {
      if ((e as Error).message === "abgebrochen") setAbgebrochen(true);
      else setFehler(`Die Änderung wurde unterbrochen: ${fehlerText(e)}`);
    }
    setSchritt("ergebnis");
    onFertig?.();
  };

  const schliessen = () => {
    abbruch.current = true;
    onSchliessen();
  };

  const geaendert = ergebnisse.filter((e) => e.ergebnis === "geaendert").length;
  const fehlerListe = ergebnisse.filter((e) => e.ergebnis === "fehler");
  const anzahl = zielAntwort?.anzahl ?? 0;

  const statusOptionen = (meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const nutzerOptionen = nutzerListe.filter((u) => u.close_user_id && u.is_active).map((u) => ({ wert: u.close_user_id as string, label: u.name }));

  return (
    <Dialog
      titel={`Massenbearbeitung · ${titel}`}
      offen={offen}
      onSchliessen={schliessen}
      breite={560}
      fuss={
        schritt === "eingabe" ? (
          <>
            <button type="button" className="knopf" onClick={schliessen}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--primaer" disabled={!eingabeOk || laedt} onClick={() => void weiter()}>
              {laedt ? "Zähle …" : "Weiter"}
            </button>
          </>
        ) : schritt === "bestaetigen" ? (
          <>
            <button type="button" className="knopf" onClick={() => setSchritt("eingabe")}>
              Zurück
            </button>
            <button type="button" className="knopf knopf--primaer" disabled={!anzahl} onClick={() => void ausfuehren()}>
              {anzahl.toLocaleString("de-DE")} {anzahl === 1 ? "Lead" : "Leads"} ändern
            </button>
          </>
        ) : schritt === "lauf" ? (
          <button type="button" className="knopf" onClick={() => (abbruch.current = true)}>
            Anhalten
          </button>
        ) : (
          <button type="button" className="knopf knopf--primaer" onClick={schliessen}>
            Fertig
          </button>
        )
      }
    >
      <div className="svd">
        {schritt === "eingabe" ? (
          <>
            <div className="svd__leise">Gilt für {beschreibung}.</div>
            <div className="svd__chips" role="tablist">
              {(
                [
                  ["status", "Status"],
                  ...(bearbeiterFeld ? [["bearbeiter", "Bearbeiter"]] : []),
                  ["feld", "Feld"],
                ] as [Art, string][]
              ).map(([k, name]) => (
                <button key={k} type="button" role="tab" aria-selected={art === k} className={`knopf knopf--klein${art === k ? " knopf--primaer" : ""}`} onClick={() => setArt(k)}>
                  {name}
                </button>
              ))}
            </div>
            {art === "status" ? (
              <div className="feld">
                <span className="feld__label">Neuer Status</span>
                <Auswahl optionen={statusOptionen} wert={statusId} platzhalter="Status wählen" onWahl={(w) => setStatusId((w as string) || null)} />
              </div>
            ) : null}
            {art === "bearbeiter" ? (
              <>
                <div className="feld">
                  <span className="feld__label">Neuer Bearbeiter</span>
                  <Auswahl optionen={nutzerOptionen} wert={bearbeiter} platzhalter="Nutzer wählen" deaktiviert={bearbeiterLeeren} onWahl={(w) => setBearbeiter((w as string) || null)} />
                </div>
                <label className="svd__haken">
                  <input type="checkbox" checked={bearbeiterLeeren} onChange={(e) => setBearbeiterLeeren(e.target.checked)} />
                  <span>Bearbeiter stattdessen entfernen</span>
                </label>
              </>
            ) : null}
            {art === "feld" ? (
              <>
                <div className="feld">
                  <span className="feld__label">Feld</span>
                  <Auswahl
                    optionen={felder.map((f) => ({ wert: f.id, label: f.name }))}
                    wert={feldId}
                    platzhalter="Feld wählen"
                    onWahl={(w) => {
                      setFeldId((w as string) || null);
                      setFeldWert(null);
                    }}
                  />
                </div>
                {feld ? (
                  <div className="feld">
                    <span className="feld__label">Neuer Wert (leer lassen = Wert entfernen)</span>
                    <FeldEingabe def={feld} wert={feldWert} onChange={setFeldWert} />
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        {schritt === "bestaetigen" && zielAntwort ? (
          <>
            <div>
              <span className="svd__zahl">{anzahl.toLocaleString("de-DE")}</span> {anzahl === 1 ? "Lead" : "Leads"} ({beschreibung}) bekommen:
            </div>
            <div className="svd__vorschau">
              <strong>{aenderungText()}</strong>
            </div>
            {zielAntwort.gekappt ? (
              <div className="hinweis hinweis--warnung">
                <Icon name="warnung" groesse={15} />
                <span>
                  Die Liste hat {zielAntwort.gesamt?.toLocaleString("de-DE")} Treffer, geändert werden höchstens {zielAntwort.maximal.toLocaleString("de-DE")} auf einmal.
                </span>
              </div>
            ) : null}
            <div className="svd__leise">Die Änderung wird sofort in Close geschrieben und lässt sich nur einzeln zurücknehmen.</div>
          </>
        ) : null}

        {schritt === "lauf" ? (
          <>
            <div>
              Leads werden geändert … {fortschritt.fertig.toLocaleString("de-DE")} von {fortschritt.gesamt.toLocaleString("de-DE")}
            </div>
            <div className="svd__balken">
              <span style={{ width: `${(100 * fortschritt.fertig) / Math.max(1, fortschritt.gesamt)}%` }} />
            </div>
          </>
        ) : null}

        {schritt === "ergebnis" ? (
          <>
            <div className={`hinweis ${fehler ? "hinweis--fehler" : abgebrochen || fehlerListe.length ? "hinweis--warnung" : "hinweis--ok"}`}>
              <Icon name={fehler || abgebrochen || fehlerListe.length ? "warnung" : "haken"} groesse={15} />
              <span>{fehler ?? (abgebrochen ? "Angehalten. Was schon geändert war, bleibt geändert." : "Fertig.")}</span>
            </div>
            <div>
              <strong className="svd__ok">{geaendert.toLocaleString("de-DE")}</strong> geändert ·{" "}
              <strong className={fehlerListe.length ? "svd__fehler" : undefined}>{fehlerListe.length.toLocaleString("de-DE")}</strong> Fehler
            </div>
            {fehlerListe.length ? (
              <div className="svd__scroll">
                <table className="svd__tabelle">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Grund</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fehlerListe.map((e) => (
                      <tr key={e.lead_id}>
                        <td>{e.name ?? e.lead_id}</td>
                        <td className="svd__fehler">{e.fehler}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}

        {fehler && schritt !== "ergebnis" ? (
          <div className="hinweis hinweis--fehler" role="alert">
            <Icon name="warnung" groesse={15} />
            <span>{fehler}</span>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
